import React, { useState, useCallback, useMemo } from 'react';
import { usePage } from '@inertiajs/react';
import { toast } from 'react-toastify';

// Simple debounce function (no lodash)
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export default function ProfileQuestionsSection({ autoSave = false }) {
  const { questions = [], profileQA = {}, urls } = usePage().props;

  // Local answer state
  const [answers, setAnswers] = useState(() => {
    const obj = {};
    questions.forEach(q => {
      obj[q.id] = profileQA[q.id]?.answer || '';
    });
    return obj;
  });

  const [processing, setProcessing] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved');

  // FIXED: Memoize placeholders so they don't change on every render
  const placeholders = useMemo(() => {
    const placeholderMap = {};
    questions.forEach(q => {
      if (Array.isArray(q.meta?.placeholder)) {
        // Use question ID as seed for consistent random selection
        const seed = q.id;
        const index = seed % q.meta.placeholder.length;
        placeholderMap[q.id] = q.meta.placeholder[index];
      } else {
        placeholderMap[q.id] = q.meta?.placeholder || 'Your answer…';
      }
    });
    return placeholderMap;
  }, [questions]); // Only recalculate if questions change, not on every render

  // Auto-save function with debounce (no lodash)
  const autoSaveQA = useCallback(
    debounce(async (qaData) => {
      if (!autoSave) return;
      
      setSaveStatus('saving');
      try {
        const response = await fetch(urls.saveQA, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
          body: JSON.stringify({ qa: qaData }),
        });

        if (response.ok) {
          setSaveStatus('saved');
        } else {
          throw new Error('Save failed');
        }
      } catch (e) {
        setSaveStatus('error');
        console.error('Auto-save Q&A error:', e);
      }
    }, 1000),
    [urls.saveQA, autoSave]
  );

  // Manual save for non-auto-save mode
  const handleSave = async () => {
    setProcessing(true);
    try {
      const response = await fetch(urls.saveQA, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
        },
        body: JSON.stringify({ qa: answers }),
      });

      if (response.ok) {
        toast.success('Profile info saved!');
      } else {
        throw new Error('Save failed');
      }
    } catch (e) {
      toast.error('Failed to save Q&A');
      console.error('Manual save Q&A error:', e);
    } finally {
      setProcessing(false);
    }
  };

  // Handle answer changes
  const handleAnswerChange = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
    
    if (autoSave) {
      setSaveStatus('saving');
      autoSaveQA(newAnswers);
    }
  };

  // Sort: answered first, then unanswered
  const sorted = [...questions].sort((a, b) => {
    const aAns = answers[a.id]?.trim();
    const bAns = answers[b.id]?.trim();
    if (aAns && !bAns) return -1;
    if (!aAns && bAns) return 1;
    return a.sort_order - b.sort_order;
  });

  const visible = showAll ? sorted : sorted.slice(0, 5);

  // Status indicator for auto-save
  const SaveStatusIndicator = () => {
    if (!autoSave) return null;
    
    switch (saveStatus) {
      case 'saving':
        return (
          <div className="text-warning small d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status" style={{width: '12px', height: '12px'}}>
              <span className="visually-hidden">Saving...</span>
            </div>
            Saving...
          </div>
        );
      case 'saved':
        return (
          <div className="text-success small d-flex align-items-center">
            <i className="fa fa-check me-2"></i>
            Saved
          </div>
        );
      case 'error':
        return (
          <div className="text-danger small d-flex align-items-center">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Error
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <h5 className="mb-0">🎭 About Me / Lightning Round</h5>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">
            {Object.values(answers).filter(a => a?.trim()).length}/{questions.length} answered
          </small>
          {autoSave && <SaveStatusIndicator />}
        </div>
      </div>

      <div className="card-body">
        {visible.map(q => {
          // FIXED: Use stable placeholder from memoized map
          const placeholder = placeholders[q.id];
          const rows = q.meta?.rows || 1;
          const multiline = q.type === 'textarea' || rows > 1;

          return (
            <div className="mb-4" key={q.id}>
              <label className="form-label fw-semibold">
                {q.prompt}
                {answers[q.id]?.trim() && (
                  <span className="badge bg-success ms-2">✓</span>
                )}
              </label>

              {multiline ? (
                <textarea
                  className="form-control"
                  rows={rows}
                  maxLength={q.meta?.max || 500}
                  value={answers[q.id]}
                  placeholder={placeholder}
                  onChange={e => handleAnswerChange(q.id, e.target.value)}
                  style={!answers[q.id]?.trim() ? {
                    fontStyle: 'italic',
                    color: '#6c757d'
                  } : {}}
                />
              ) : (
                <input
                  type="text"
                  className="form-control"
                  value={answers[q.id]}
                  placeholder={placeholder}
                  maxLength={q.meta?.max || 200}
                  onChange={e => handleAnswerChange(q.id, e.target.value)}
                  style={!answers[q.id]?.trim() ? {
                    fontStyle: 'italic',
                    color: '#6c757d'
                  } : {}}
                />
              )}
            </div>
          );
        })}

        {questions.length > 5 && (
          <div className="text-center mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? (
                <>
                  <i className="fa fa-chevron-up me-1"></i>
                  Show Less
                </>
              ) : (
                <>
                  <i className="fa fa-chevron-down me-1"></i>
                  Show All ({questions.length - 5} more)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Only show save button if not auto-save mode */}
      {!autoSave && (
        <div className="card-footer text-end">
          <button 
            className="btn btn-primary" 
            onClick={handleSave} 
            disabled={processing}
          >
            <i className="fa fa-save me-1"></i>
            {processing ? 'Saving...' : 'Save Q&A'}
          </button>
        </div>
      )}
    </div>
  );
}